import * as React from "react";
import { Badge } from "../components/Badge";
import { Icon } from "../components/Icon";
import { CalculationFinding, severityOf } from "../api/errors";

/**
 * Findings - the engine's validation output.
 *
 * They are computed on demand and never persisted, so a list of them is always a live answer to
 * "would this work right now", not a history. Severity arrives as a number (the backend enum
 * serializes that way), which is why `severityOf` exists rather than a direct comparison.
 */

const TONES = {
    Error: "danger",
    Warning: "warning",
    Information: "info",
} as const;

const ICONS = {
    Error: ["fad", "circle-exclamation"],
    Warning: ["fad", "triangle-exclamation"],
    Information: ["fad", "circle-info"],
} as const;

export interface FindingListProps {
    findings?: CalculationFinding[];
    /** Shown when there are none. Omit to render nothing at all. */
    emptyMessage?: React.ReactNode;
    /** Cap the list and summarise the rest - for inline panels where findings are secondary. */
    limit?: number;
}

export const FindingList: React.FC<FindingListProps> = ({ findings, emptyMessage, limit }) => {
    const all = findings ?? [];

    if (!all.length) {
        return emptyMessage ? <p className="text-sm text-text-weak">{emptyMessage}</p> : null;
    }

    // Errors first - they are the ones that stop a run.
    const order = { Error: 0, Warning: 1, Information: 2 };
    const sorted = all.slice().sort((a, b) => order[severityOf(a)] - order[severityOf(b)]);
    const shown = limit ? sorted.slice(0, limit) : sorted;
    const hidden = sorted.length - shown.length;

    return (
        <div className="space-y-1.5">
            {shown.map((finding, index) => {
                const severity = severityOf(finding);
                return (
                    <div
                        key={`${finding.code}-${index}`}
                        className={`flex items-start gap-2 rounded-md border p-2.5 text-sm ${
                            severity === "Error"
                                ? "border-danger-border bg-danger-soft text-danger-text"
                                : severity === "Warning"
                                ? "border-warning-border bg-warning-soft text-warning-text"
                                : "border-info-border bg-info-soft text-info-text"
                        }`}
                    >
                        <Icon icon={ICONS[severity]} className="mt-0.5" />
                        <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                                <Badge tone={TONES[severity]}>{finding.code}</Badge>
                                {finding.studentID && <span className="font-mono text-xs opacity-75">student {finding.studentID}</span>}
                            </div>
                            <p className="mt-0.5">{finding.message}</p>
                        </div>
                    </div>
                );
            })}

            {hidden > 0 && <p className="text-xs text-text-weak">and {hidden} more.</p>}
        </div>
    );
};

/** Counts by severity, for a compact summary next to a heading. */
export const FindingSummary: React.FC<{ findings?: CalculationFinding[] }> = ({ findings }) => {
    const all = findings ?? [];
    if (!all.length) return null;

    const counts = { Error: 0, Warning: 0, Information: 0 };
    all.forEach((f) => counts[severityOf(f)]++);

    return (
        <div className="flex items-center gap-2">
            {counts.Error > 0 && <Badge tone="danger">{counts.Error} error{counts.Error === 1 ? "" : "s"}</Badge>}
            {counts.Warning > 0 && <Badge tone="warning">{counts.Warning} warning{counts.Warning === 1 ? "" : "s"}</Badge>}
            {counts.Information > 0 && <Badge tone="info">{counts.Information} note{counts.Information === 1 ? "" : "s"}</Badge>}
        </div>
    );
};

export default FindingList;
